import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../common/contexts/useToast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UserCreateInterface,
  UsersDataResponseInterface,
} from "../../common/interfaces/usersData.interface";
import * as Yup from "yup";
import MainHome from "../MainHome";
import { ReactComponent as InfoIcon } from "../../common/assats/icons/infoIcon.svg";
import ButtonCustom from "../common/Button";
import FormBuilder, { FormBuilderInterface } from "../common/FormBuilder";
import {
  createUser,
  getUsersData,
  getUserDataForCreation,
  updateUser,
} from "../../common/api/apiCalls";
import { useQuery } from "@tanstack/react-query";
import { Positions } from "../common/general/positions";
import userCreationValidation from "../../common/validations/UsersCreateValidationSchemas";
import {
  getRoleDisplayNames,
  getRolesByRoleName,
} from "../../common/function/generalFunctions";
import useUserStore from "../../common/store/user.store";
import { roles } from "../../common/consts/roles";

const SYSTEM_MODES = {
  Product: "Product",
  Research: "Research",
  Demo: "Demo",
  Clinical: "Clinical",
} as const;

const roleToSystemModes: Record<string, string[]> = {
  "Super Admin": [SYSTEM_MODES.Product, SYSTEM_MODES.Research, SYSTEM_MODES.Demo],
  "System Admin": [SYSTEM_MODES.Product, SYSTEM_MODES.Research, SYSTEM_MODES.Demo],
  "Root User": [SYSTEM_MODES.Product, SYSTEM_MODES.Research, SYSTEM_MODES.Demo],
  "Site Admin": [SYSTEM_MODES.Product, SYSTEM_MODES.Research, SYSTEM_MODES.Demo],
  "Clinical User": [SYSTEM_MODES.Product],
  "Non Clinical User": [SYSTEM_MODES.Product],
  "Clinical Staff": [SYSTEM_MODES.Clinical],
};

const getSelectedModesFromState = (state: any): string[] => {
  const modeSource = state?.systemMode || state?.tableProps?.systemMode || state || {};
  return [
    modeSource?.isSystemModeProduct && SYSTEM_MODES.Product,
    modeSource?.isSystemModeResearch && SYSTEM_MODES.Research,
    modeSource?.isSystemModeDemo && SYSTEM_MODES.Demo,
    modeSource?.isSystemModeClinical && SYSTEM_MODES.Clinical,
  ].filter(Boolean) as string[];
};

const parseSystemModeSelection = (value?: string): string[] => {
  try {
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
};

const getAllowedModesForRole = (
  role: string,
  creator: any,
  creatorModes: string[]
): string[] => {
  const roleModes = roleToSystemModes[role] || [];

  if (creator?.role !== roles.SiteAdmin || role !== "Site Admin") {
    return roleModes;
  }

  return roleModes.filter((mode) => creatorModes.includes(mode));
};

const userInformationCreateConfig: FormBuilderInterface[] = [
  {
    name: "honorific",
    label: "general.honorific.title",
    type: "dropdown",
    optionsArray: [
      "general.honorific.mr",
      "general.honorific.mrs",
      "general.honorific.ms",
      "general.honorific.dr",
    ],
    required: true,
    translateOffset: false,
    translateLabel: true,
    translateOptions: true,
  },
  {
    name: "firstName",
    label: "usersList.usersListCreate.firstName",
    type: "text",
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "middleName",
    label: "usersList.usersListCreate.middleName",
    type: "text",
    required: false,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "lastName",
    label: "usersList.usersListCreate.lastName",
    type: "text",
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "email",
    label: "usersList.usersListCreate.userEmail",
    type: "text",
    required: true,
    isDisabled: false,
    translateOffset: false,
    translateLabel: true,
  },
  
  {
    name: "relatedSite",
    label: "usersList.usersListCreate.relatedSite",
    type: "dropdown",
    optionsArray: [],
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "position",
    label: "usersList.usersListCreate.userPosition",
    type: "dropdown",
    optionsArray: Positions,
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "role",
    label: "usersList.usersListCreate.userRole",
    type: "dropdown",
    optionsArray: [],
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
  {
    name: "systemModeSelection",
    label: "usersList.usersListCreate.systemMode",
    type: "multiSelect",
    optionsArray: [],
    required: true,
    translateOffset: false,
    translateLabel: true,
    isDisabled: true,
    disableSearch: true,
  },
  {
    name: "phoneNumber",
    label: "usersList.usersListCreate.userPhone",
    type: "phone",
    required: true,
    translateOffset: false,
    translateLabel: true,
  },
];

const UserCreatePage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const locationUserDataState = location.state || null;
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formConfig, setFormConfig] = useState<FormBuilderInterface[]>(userInformationCreateConfig);


  const { user } = useUserStore();

  const [userData, setUserData] = useState<UserCreateInterface>({
    honorific: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    relatedSite: "",
    position: "",
    role: "",
    systemModeSelection: "",
    isSystemModeDemo: false,
    isSystemModeProduct: false,
    isSystemModeClinical: false,
    isSystemModeResearch: false,
  });

  const {
    data: response,
    isPending,
    error,
  } = useQuery({
    queryKey: [],
    queryFn: () => getUserDataForCreation(),
  });

  const { data: currentUserResponse } = useQuery({
    queryKey: ["current-user-system-modes", user?.id, user?.username],
    queryFn: () =>
      getUsersData({
        sortKey: "username",
        sortOrder: "asc",
        search: user?.username || "",
        page: 1,
        limit: 10,
      }),
    enabled: user?.role === roles.SiteAdmin && !!user?.username,
  });

  const currentUserWithSystemModes = useMemo(() => {
    const currentUserId = user?.id;
    const currentUsername = user?.username?.toLowerCase();

    return (currentUserResponse?.data?.users ?? []).find(
      (u: UsersDataResponseInterface) =>
        (currentUserId && String(u.id) === String(currentUserId)) ||
        (currentUsername && (u.username ?? "").toLowerCase() === currentUsername)
    );
  }, [currentUserResponse, user?.id, user?.username]);

  const currentSiteAdminModes = useMemo(
    () => getSelectedModesFromState(currentUserWithSystemModes || user),
    [currentUserWithSystemModes, user]
  );

  const isCurrentSiteAdminModesReady =
    user?.role !== roles.SiteAdmin ||
    currentSiteAdminModes.length > 0 ||
    !!currentUserResponse;

  useEffect(() => {
    if (!locationUserDataState || !locationUserDataState?.username) {
      return;
    } else if (!!locationUserDataState) {
      setIsEdit(true);
    }
    const selectedModes = getSelectedModesFromState(locationUserDataState);
    setUserData({
      firstName: locationUserDataState?.tableProps?.firstName || "",
      middleName: locationUserDataState?.tableProps?.middleName || "",
      lastName: locationUserDataState?.tableProps?.lastName || "",
      email: locationUserDataState?.username || "",
      phoneNumber: locationUserDataState?.phoneNumber || "",
      relatedSite: locationUserDataState?.tableProps?.sites?.[0]?.siteName,
      position: locationUserDataState?.position,
      role: locationUserDataState.tableProps?.roles?.[0]?.name && getRoleDisplayNames(false,locationUserDataState.tableProps?.roles?.[0]?.name),
      honorific: locationUserDataState?.tableProps?.honorific || "",
      systemModeSelection: JSON.stringify(selectedModes),
      isSystemModeDemo: selectedModes.includes(SYSTEM_MODES.Demo),
      isSystemModeProduct: selectedModes.includes(SYSTEM_MODES.Product),
      isSystemModeClinical: selectedModes.includes(SYSTEM_MODES.Clinical),
      isSystemModeResearch: selectedModes.includes(SYSTEM_MODES.Research),
    });
  }, [locationUserDataState]);

  const navigateToUsersList = () => {
    navigate("/Home/Users", { replace: true });
    return;
  };

  const handleInputChange = (name: string, value: any) => {
    setUserData((prev) => {
      if (name === "role") {
        return {
          ...prev,
          role: value,
          systemModeSelection: "",
          isSystemModeDemo: false,
          isSystemModeProduct: false,
          isSystemModeClinical: false,
          isSystemModeResearch: false,
        };
      }

      if (name === "systemModeSelection") {
        const parsedModes = parseSystemModeSelection(value);
        const allowedModes = getAllowedModesForRole(prev.role, user, currentSiteAdminModes);
        const filteredModes = user?.role === roles.SiteAdmin && prev.role === "Site Admin"
          ? parsedModes.filter((mode) => allowedModes.includes(mode))
          : parsedModes;

        return {
          ...prev,
          systemModeSelection: JSON.stringify(filteredModes),
          isSystemModeDemo: filteredModes.includes(SYSTEM_MODES.Demo),
          isSystemModeProduct: filteredModes.includes(SYSTEM_MODES.Product),
          isSystemModeClinical: filteredModes.includes(SYSTEM_MODES.Clinical),
          isSystemModeResearch: filteredModes.includes(SYSTEM_MODES.Research),
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const updateConfigData = () => {
    if (!response?.data.roles?.length || !response?.data.sites?.length || !response?.success) {
      return;
    }
  
    const updatedConfig = userInformationCreateConfig.map((field) => {
      if (field.name === "role") {
        const userRole = user?.role || "";
        const arrRoles = getRolesByRoleName(userRole);
        return { ...field, optionsArray: arrRoles as string[] };
      }

      if (field.name === "systemModeSelection") {
        const allowedModes = getAllowedModesForRole(userData.role, user, currentSiteAdminModes);
        const selectedModes = parseSystemModeSelection(userData.systemModeSelection);
        const visibleSelectedModes = user?.role === roles.SiteAdmin && userData.role === "Site Admin"
          ? selectedModes.filter((mode) => allowedModes.includes(mode))
          : selectedModes;
        const isWaitingForSiteAdminModes =
          user?.role === roles.SiteAdmin &&
          userData.role === "Site Admin" &&
          !isCurrentSiteAdminModesReady;

        return {
          ...field,
          optionsArray: Array.from(new Set([...allowedModes, ...visibleSelectedModes])),
          isDisabled: !userData.role || isWaitingForSiteAdminModes,
        };
      }
  
      if (field.name === "relatedSite") {
        const updatedField = {
          ...field,
          optionsArray: [...response.data.sites],
        };
  
        if (user?.role && user?.role === roles.SiteAdmin) {
          updatedField.isDisabled = true;
        }
        return updatedField;
      }
  
      if ((isEdit || locationUserDataState !== null) && field.name === "email") {
        return { ...field, isDisabled: true };
      } else if (field.name === "email") {
        return { ...field, isDisabled: false };
      }
  
      if (field.name === "position") {
        return { ...field, optionsArray: Positions.map((position) => t(position)) };
      }
  
      return field;
    });
  
    // Update state
    setFormConfig(updatedConfig);
  };

  useEffect(() => {
    updateConfigData();
  }, [response, userData.role, user, currentSiteAdminModes, isEdit, locationUserDataState, t]); 

  useEffect(() => {
    if (
      user?.role !== roles.SiteAdmin ||
      userData.role !== "Site Admin" ||
      !isCurrentSiteAdminModesReady ||
      !userData.systemModeSelection
    ) {
      return;
    }

    const allowedModes = getAllowedModesForRole(userData.role, user, currentSiteAdminModes);
    const selectedModes = parseSystemModeSelection(userData.systemModeSelection);
    const filteredModes = selectedModes.filter((mode) => allowedModes.includes(mode));

    if (filteredModes.length === selectedModes.length) {
      return;
    }

    setUserData((prev) => ({
      ...prev,
      systemModeSelection: JSON.stringify(filteredModes),
      isSystemModeDemo: filteredModes.includes(SYSTEM_MODES.Demo),
      isSystemModeProduct: filteredModes.includes(SYSTEM_MODES.Product),
      isSystemModeClinical: filteredModes.includes(SYSTEM_MODES.Clinical),
      isSystemModeResearch: filteredModes.includes(SYSTEM_MODES.Research),
    }));
  }, [user, currentSiteAdminModes, isCurrentSiteAdminModesReady, userData.role, userData.systemModeSelection]);

  useEffect(() => {
    if (user?.role && user?.role === roles.SiteAdmin && user?.site) {
      setUserData(prev => ({ ...prev, relatedSite: user.site }));
    }
  }, [user?.site, user?.role]);

  const handleSubmit = async () => {
    try {
      await userCreationValidation.validate(userData, { abortEarly: false });
      const selectedModes = parseSystemModeSelection(userData.systemModeSelection);
      const allowedModes = getAllowedModesForRole(userData.role, user, currentSiteAdminModes);
      const isWaitingForSiteAdminModes =
        user?.role === roles.SiteAdmin &&
        userData.role === "Site Admin" &&
        !isCurrentSiteAdminModesReady;

      if (isWaitingForSiteAdminModes) {
        return;
      }

      const submittedModes = user?.role === roles.SiteAdmin && userData.role === "Site Admin"
        ? selectedModes.filter((mode) => allowedModes.includes(mode))
        : selectedModes;

      if (!submittedModes.length) {
        setErrors((prev) => ({
          ...prev,
          systemModeSelection: t("usersList.usersListCreate.formErrors.systemModeRequired"),
        }));
        return;
      }

      const data: UserCreateInterface = {
        honorific: userData.honorific,
        firstName: userData.firstName,
        middleName: userData.middleName,
        lastName: userData.lastName,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        relatedSite: userData.relatedSite,
        position: userData.position,
        role: userData.role,

          isSystemModeDemo: submittedModes.includes(SYSTEM_MODES.Demo),
          isSystemModeProduct: submittedModes.includes(SYSTEM_MODES.Product),
          isSystemModeClinical: submittedModes.includes(SYSTEM_MODES.Clinical),
          isSystemModeResearch: submittedModes.includes(SYSTEM_MODES.Research),

      };
      let res = null;

      if (isEdit) {
        res = await updateUser(data);
      } else {
        res = await createUser(data);
      }
      if (res.success) {
        showToast(
          "success",
          "Success",
          isEdit
            ? t("usersList.usersListCreate.updateSuccess")
            : t("usersList.usersListCreate.addSuccess")
        );
        navigateToUsersList();
      } else {
        showToast("error", "Error", t(res.data.errorMessage));
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = t(error.message || "general.errors.emptyField");
          }
        });
        setErrors(validationErrors);
      }
    }
  };

  return (
    <MainHome>
      <div className="page-container w-100 h-100 p-5 border rounded background-primary-white">
        <div className="pr-5 pl-5 h-100 d-flex flex-column">
          <div className="mb-4">
            <label className="fw-bold heading-h3">
              {isEdit
                ? t("usersList.usersListCreate.editUser")
                : t("usersList.usersListCreate.addNewUser")}
            </label>
            <hr className="my-4" />
            <div className="ms-2 d-flex align-items-end">
              <InfoIcon />
              <label className="ms-2 heading-h4">
                {t("usersList.usersListCreate.userInformation")}
              </label>
            </div>
          </div>
          <div className="flex-grow-1">
            <FormBuilder
              fields={formConfig}
              handleInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e.target.name, e.target.value)
              }
              values={userData}
              showErrors={true}
              errors={errors}
              translateErrors={true}
            />
          </div>
          <hr className="form-footer-divider" />
          <div className="form-footer">
            <ButtonCustom
              type="button"
              onClick={navigateToUsersList}
              className="btn-secondary btn-lg"
              context={t("general.buttons.cancel")}
            />
            <ButtonCustom
              type="button"
              onClick={handleSubmit}
              className="btn-primary btn-lg"
              context={
                isEdit
                  ? t("general.buttons.save")
                  : t("usersList.usersListCreate.saveUser")
              }
            />
          </div>
        </div>
      </div>
    </MainHome>
  );
};

export default UserCreatePage;
